import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  Hammer,
  Calendar,
  Store,
  IndianRupee,
  FileText,
  History,
  Loader2,
  Package,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import { Equipment, statusMap, statusRevMap } from "./EquipmentConstants";
import {
  getEquipmentItemDetail,
  updateEquipmentItem,
  createServiceLog,
} from "../../../../Redux/ApiCalls/Dashboard/EquipmentAPI";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Field,
  FieldGrid,
  Label,
  Input,
  Select,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

interface ServiceModalProps {
  equipment: Equipment;
  onClose: () => void;
  onUpdate: (updated: Equipment) => void;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const EquipmentCard = styled.div`
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.08) 0%, rgba(206, 86, 176, 0.06) 100%);
  border: 1px solid rgba(201, 77, 170, 0.12);
  margin-bottom: 18px;
`;

const EqName = styled.div`
  font-family: Urbanist, sans-serif;
  font-weight: 800;
  font-size: 16px;
  color: #171717;
`;

const EqMeta = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.55);
  margin: 4px 0 12px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`;

const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(23, 23, 23, 0.5);
  margin-bottom: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  padding: 14px 16px;
  color: #171717;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  margin: 20px 0;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
`;

const HistoryItem = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const RecordHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

const RecordDate = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.65);
`;

const RecordCost = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  color: #31a66a;
  font-weight: 800;
`;

const RecordVendor = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.5);
`;

const RecordNotes = styled.p`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.72);
  margin: 8px 0 0;
  line-height: 1.55;
`;

const NoHistoryText = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.45);
  text-align: center;
  padding: 20px;
  border-radius: 14px;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  background: #fafafa;
`;

const ServiceHistoryModal: React.FC<ServiceModalProps> = ({ equipment, onClose, onUpdate }) => {
  const [status, setStatus] = useState<Equipment["status"]>(equipment.status);
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [nextDueDate, setNextDueDate] = useState(equipment.nextServiceDueDate || "");
  const [vendor, setVendor] = useState("");
  const [cost, setCost] = useState("");
  const [notes, setNotes] = useState("");
  const [serviceHistory, setServiceHistory] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadDetails = async () => {
      try {
        const detail = await getEquipmentItemDetail(equipment.id);
        setServiceHistory(detail.service_logs || []);
        setStatus((statusMap[detail.status] as Equipment["status"]) || "Available");
      } catch {
        /* ignore */
      }
    };
    loadDetails();
  }, [equipment.id]);

  const handleAddRecord = async () => {
    if (!vendor.trim()) {
      showErrorToast("Please specify service vendor");
      return;
    }
    if (!notes.trim()) {
      showErrorToast("Please describe the service action performed");
      return;
    }
    if (!nextDueDate) {
      showErrorToast("Please select the next service due date");
      return;
    }
    setSubmitting(true);
    try {
      await createServiceLog(equipment.id, {
        service_date: serviceDate,
        vendor: vendor.trim(),
        cost: cost ? cost : "0.00",
        notes: notes.trim(),
        next_service_date: nextDueDate,
      });
      showSuccessToast("Servicing record logged successfully!");
      onUpdate(null as any);
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to log service record");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus: Equipment["status"]) => {
    setStatus(newStatus);
    try {
      await updateEquipmentItem(equipment.id, {
        status: (statusRevMap[newStatus] || "available") as any,
      });
      showSuccessToast(`Equipment status updated to ${newStatus}`);
      onUpdate(null as any);
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to update status");
    }
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Hammer size={11} />
                  Maintenance
                </HeroBadge>
                <ModalTitle>Servicing & Warranty Log</ModalTitle>
                <ModalSubtitle>
                  Log service sessions and track warranty for {equipment.name}.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            <EquipmentCard>
              <EqName>{equipment.name}</EqName>
              <EqMeta>
                S/N: {equipment.serialNumber} · Warranty exp: {equipment.warrantyExpiry || "N/A"}
              </EqMeta>
              <StatusRow>
                <Label htmlFor="eq-status" style={{ margin: 0 }}>
                  <FormLabelIcon icon={Package} />
                  Equipment Status
                </Label>
                <Select
                  id="eq-status"
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value as Equipment["status"])}
                  style={{ maxWidth: 180, height: 42 }}
                >
                  <option value="Available">Available</option>
                  <option value="Assigned">Assigned</option>
                  <option value="Under Service">Under Service</option>
                  <option value="Sold">Sold</option>
                  <option value="Retired">Retired</option>
                </Select>
              </StatusRow>
            </EquipmentCard>

            <FormWrapper>
              <SectionLabel>
                <FormLabelIcon icon={Hammer} />
                Log new servicing session
              </SectionLabel>
              <FieldGrid>
                <Field>
                  <Label htmlFor="service-date">
                    <FormLabelIcon icon={Calendar} />
                    Service Date
                  </Label>
                  <BrandDatePicker
                    name="service-date"
                    value={serviceDate}
                    onChange={(v) => setServiceDate(v)}
                  />
                </Field>
                <Field>
                  <Label htmlFor="next-due">
                    <FormLabelIcon icon={Calendar} />
                    Next Due Date
                  </Label>
                  <BrandDatePicker
                    name="next-due"
                    value={nextDueDate}
                    onChange={(v) => setNextDueDate(v)}
                  />
                </Field>
              </FieldGrid>
              <FieldGrid>
                <Field>
                  <Label htmlFor="vendor">
                    <FormLabelIcon icon={Store} />
                    Vendor / Technician
                  </Label>
                  <Input
                    id="vendor"
                    type="text"
                    placeholder="e.g. Sony Service Center"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label htmlFor="cost">
                    <FormLabelIcon icon={IndianRupee} />
                    Service Cost (₹)
                  </Label>
                  <Input
                    id="cost"
                    type="number"
                    placeholder="e.g. 1500"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                  />
                </Field>
              </FieldGrid>
              <Field>
                <Label htmlFor="service-notes">
                  <FormLabelIcon icon={FileText} />
                  Notes & Actions Performed
                </Label>
                <TextArea
                  id="service-notes"
                  placeholder="Describe the issue, work completed, or components replaced..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
              <PrimaryBtn type="button" onClick={handleAddRecord} disabled={submitting}>
                {submitting ? <Spinner size={16} /> : <Hammer size={16} />}
                {submitting ? "Logging..." : "Log Service & Schedule Next Reminder"}
              </PrimaryBtn>
            </FormWrapper>

            <Divider />

            <SectionLabel>
              <FormLabelIcon icon={History} />
              Servicing history trail
            </SectionLabel>
            {serviceHistory.length === 0 ? (
              <NoHistoryText>No previous service logs exist for this gear.</NoHistoryText>
            ) : (
              <HistoryList>
                {serviceHistory.map((record, idx) => (
                  <HistoryItem key={idx}>
                    <RecordHeader>
                      <RecordDate>
                        <Calendar size={12} /> {record.service_date}
                      </RecordDate>
                      <RecordCost>
                        {Number(record.cost) > 0 ? `₹${record.cost}` : "Free / Warranty"}
                      </RecordCost>
                    </RecordHeader>
                    <RecordVendor>By: {record.vendor || "Unknown Vendor"}</RecordVendor>
                    <RecordNotes>{record.notes}</RecordNotes>
                  </HistoryItem>
                ))}
              </HistoryList>
            )}
          </ModalBody>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};

export default ServiceHistoryModal;
